import { defineMessage, defineMessages, MessageDescriptor } from 'react-intl';
import { ReelStatus } from '../../types';
import { ReelFailKey } from './reelProgressMessages';
import { aiBudgetMessages } from '../../aiBudgetMessages';

/** The copy of the preview, the approve sheet and the reject sheet (design frames c, d, e). */
export const reelReviewMessages = defineMessages({
  titleApproved: { id: 'app.sml.promote.reels.preview.approved.title', defaultMessage: 'Your reel is approved' },
  titleRejected: { id: 'app.sml.promote.reels.preview.rejected.title', defaultMessage: 'You rejected this reel' },
  subtitleReady: {
    id: 'app.sml.promote.reels.preview.subtitle',
    defaultMessage: 'Watch it, then approve it into Promote or reject it with a note.',
  },
  newReel: { id: 'app.sml.promote.reels.preview.newReel', defaultMessage: 'New reel' },
  player: { id: 'app.sml.promote.reels.preview.player', defaultMessage: 'The reel for {release}' },
  captions: { id: 'app.sml.promote.reels.preview.captions', defaultMessage: 'Captions on screen' },
  voiceover: { id: 'app.sml.promote.reels.preview.voiceover', defaultMessage: 'Voiceover' },
  voiceHint: { id: 'app.sml.promote.reels.preview.voiceHint', defaultMessage: '{voice} · {tone}' },
  postCaption: { id: 'app.sml.promote.reels.preview.postCaption', defaultMessage: 'Suggested post caption' },
  postCaptionHint: {
    id: 'app.sml.promote.reels.preview.postCaption.hint',
    defaultMessage: 'You can change it when you approve',
  },
  approve: { id: 'app.sml.promote.reels.preview.approve', defaultMessage: 'Approve' },
  reject: { id: 'app.sml.promote.reels.preview.reject', defaultMessage: 'Reject' },
  approveNote: {
    id: 'app.sml.promote.reels.preview.approveNote',
    defaultMessage: 'Approve saves draft posts. Nothing goes out until you schedule it.',
  },
  approvedTitle: {
    id: 'app.sml.promote.reels.preview.approved.alert.title',
    defaultMessage: 'You approved this reel.',
  },
  approvedBody: {
    id: 'app.sml.promote.reels.preview.approved.alert.body',
    defaultMessage: 'Its draft posts are in the Calendar. Pick a time there to post them.',
  },
  openCalendar: { id: 'app.sml.promote.reels.preview.openCalendar', defaultMessage: 'Open the Calendar' },
  download: { id: 'app.sml.promote.reels.preview.download', defaultMessage: 'Download' },
  toast: {
    id: 'app.sml.promote.reels.preview.toast',
    defaultMessage: '{count, plural, one {# draft post is} other {# draft posts are}} in the Calendar.',
  },
  yourNotes: { id: 'app.sml.promote.reels.preview.rejected.notes', defaultMessage: 'Your notes' },
  noChild: { id: 'app.sml.promote.reels.preview.rejected.noChild', defaultMessage: 'No new reel came from this one.' },
  hasChild: { id: 'app.sml.promote.reels.preview.rejected.hasChild', defaultMessage: 'A new reel came from this one.' },
  openChild: { id: 'app.sml.promote.reels.preview.rejected.openChild', defaultMessage: 'Open the new reel' },
  makeAnother: { id: 'app.sml.promote.reels.preview.makeAnother', defaultMessage: 'Make another' },
  makeAnotherHint: {
    id: 'app.sml.promote.reels.preview.makeAnother.hint',
    defaultMessage:
      'Uses 1 video credit · {limit, plural, one {# video credit} other {# video credits}} a month on your plan',
  },
  conflictApproveTitle: {
    id: 'app.sml.promote.reels.conflict.approve.title',
    defaultMessage: 'This reel cannot be approved now.',
  },
  conflictApproveBody: {
    id: 'app.sml.promote.reels.conflict.approve.body',
    defaultMessage: 'Somebody approved or rejected it already. We loaded the reel again.',
  },
  conflictRegenerateTitle: {
    id: 'app.sml.promote.reels.conflict.regenerate.title',
    defaultMessage: 'This reel cannot make another now.',
  },
  conflictRegenerateBody: {
    id: 'app.sml.promote.reels.conflict.regenerate.body',
    defaultMessage: 'A new reel came from it already. We loaded the reel again.',
  },
  refusedTitle: {
    id: 'app.sml.promote.reels.refused.title',
    defaultMessage: 'We rejected the reel, but no new reel started.',
  },
  refusedQuota: {
    id: 'app.sml.promote.reels.refused.quota',
    defaultMessage: 'You have no video credits left. Buy credits now, or make the new reel next month.',
  },
  refusedQuotaDate: {
    id: 'app.sml.promote.reels.refused.quota.date',
    defaultMessage: 'You have no video credits left. Buy credits now, or make the new reel after {date}.',
  },
  refusedFeature: {
    id: 'app.sml.promote.reels.refused.feature',
    defaultMessage: 'AI reels are not open for your workspace now.',
  },
  refusedUnavailable: {
    id: 'app.sml.promote.reels.refused.unavailable',
    defaultMessage: 'AI reels are not available right now. No video credit was used.',
  },
  upgrade: { id: 'app.sml.promote.reels.refused.upgrade', defaultMessage: 'Upgrade' },
  regenerateLater: { id: 'app.sml.promote.reels.refused.later', defaultMessage: 'Regenerate later' },
  historyTitle: { id: 'app.sml.promote.reels.history.title', defaultMessage: 'Reels for {release}' },
  historyTitleNoRelease: {
    id: 'app.sml.promote.reels.history.titleNoRelease',
    defaultMessage: 'Reels for this release',
  },
  historyOrder: { id: 'app.sml.promote.reels.history.order', defaultMessage: 'Newest first' },
  historyNow: { id: 'app.sml.promote.reels.history.now', defaultMessage: 'Now' },
  historyWhy: { id: 'app.sml.promote.reels.history.whyTheme', defaultMessage: '{theme} · {voice}' },
  historyQuote: { id: 'app.sml.promote.reels.history.quote', defaultMessage: '“{reason}”' },
  historyOpen: {
    id: 'app.sml.promote.reels.history.openWithWhy',
    defaultMessage: 'Open the reel of {date}, {status}: {why}',
  },
});

/** The copy of the approve sheet (design d). */
export const reelApproveMessages = defineMessages({
  title: { id: 'app.sml.promote.reels.approve.title', defaultMessage: 'Approve this reel' },
  intro: {
    id: 'app.sml.promote.reels.approve.intro',
    defaultMessage: 'MUSIC24 saves it as draft posts. You pick the time in the Calendar or the Composer.',
  },
  caption: { id: 'app.sml.promote.reels.approve.caption', defaultMessage: 'Post caption' },
  captionHint: {
    id: 'app.sml.promote.reels.approve.caption.hint',
    defaultMessage: 'The AI wrote it. Change it if you like.',
  },
  captionCount: { id: 'app.sml.promote.reels.approve.caption.count', defaultMessage: '{count} / {max}' },
  captionTooLong: {
    id: 'app.sml.promote.reels.approve.caption.tooLong',
    defaultMessage: 'Make the caption {max} characters or shorter.',
  },
  captionEmpty: { id: 'app.sml.promote.reels.approve.caption.empty', defaultMessage: 'Write a caption for the posts.' },
  captionShortened: {
    id: 'app.sml.promote.reels.approve.caption.shortened',
    defaultMessage: '{platform} takes {max} characters. Its post gets a shorter caption.',
  },
  channels: { id: 'app.sml.promote.reels.approve.channels', defaultMessage: 'Channels' },
  channelsHint: {
    id: 'app.sml.promote.reels.approve.channels.hint',
    defaultMessage: 'One draft post for each channel',
  },
  signInAgain: { id: 'app.sml.promote.reels.approve.signInAgain', defaultMessage: 'Sign in again' },
  draftNote: {
    id: 'app.sml.promote.reels.approve.draftNote',
    defaultMessage: 'Draft posts do not count toward your monthly posts.',
  },
  cancel: { id: 'app.sml.promote.reels.approve.cancel', defaultMessage: 'Cancel' },
  save: {
    id: 'app.sml.promote.reels.approve.save',
    defaultMessage: 'Save as {count, plural, one {# draft post} other {# draft posts}}',
  },
  noChannelTitle: { id: 'app.sml.promote.reels.approve.noChannel.title', defaultMessage: 'No channel is connected' },
  noChannelBody: {
    id: 'app.sml.promote.reels.approve.noChannel.body',
    defaultMessage: 'Connect TikTok, Instagram or another channel first. The reel stays ready here.',
  },
  connect: { id: 'app.sml.promote.reels.approve.connect', defaultMessage: 'Connect a channel' },
});

/** The copy of the reject sheet (design e). */
export const reelRejectMessages = defineMessages({
  title: { id: 'app.sml.promote.reels.reject.title', defaultMessage: 'Reject this reel' },
  intro: {
    id: 'app.sml.promote.reels.reject.intro',
    defaultMessage: 'Tell the AI what to change. It makes a new reel with your notes.',
  },
  whatIsWrong: { id: 'app.sml.promote.reels.reject.whatIsWrong', defaultMessage: 'What is wrong?' },
  notes: { id: 'app.sml.promote.reels.reject.notes', defaultMessage: 'Notes for the AI' },
  optional: { id: 'app.sml.promote.reels.reject.optional', defaultMessage: 'Optional' },
  count: { id: 'app.sml.promote.reels.reject.count', defaultMessage: '{count} / {max}' },
  tooLong: {
    id: 'app.sml.promote.reels.reject.tooLong',
    defaultMessage: 'Make the reason {max} characters or shorter. The count includes the picked chips.',
  },
  usesOne: { id: 'app.sml.promote.reels.reject.usesOne', defaultMessage: 'A new reel uses 1 video credit' },
  allowance: {
    id: 'app.sml.promote.reels.reject.allowance',
    defaultMessage: '{limit, plural, one {# video credit} other {# video credits}} a month',
  },
  staysInHistory: {
    id: 'app.sml.promote.reels.reject.staysInHistory',
    defaultMessage: 'This reel stays in the history of {release}.',
  },
  staysInHistoryNoRelease: {
    id: 'app.sml.promote.reels.reject.staysInHistoryNoRelease',
    defaultMessage: 'This reel stays in the history of the release.',
  },
  cancel: { id: 'app.sml.promote.reels.reject.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'app.sml.promote.reels.reject.submit', defaultMessage: 'Reject and make another' },
});

/** The reason chips of the reject sheet, in design order. */
export const reelRejectChips = ['tooFast', 'wrongMood', 'captionsWrong', 'voiceoverOff', 'other'] as const;
export type ReelRejectChip = (typeof reelRejectChips)[number];

export const reelRejectChipMessages: Record<ReelRejectChip, MessageDescriptor> = defineMessages({
  tooFast: { id: 'app.sml.promote.reels.reject.chip.tooFast', defaultMessage: 'Too fast' },
  wrongMood: { id: 'app.sml.promote.reels.reject.chip.wrongMood', defaultMessage: 'Wrong mood' },
  captionsWrong: { id: 'app.sml.promote.reels.reject.chip.captionsWrong', defaultMessage: 'Captions wrong' },
  voiceoverOff: { id: 'app.sml.promote.reels.reject.chip.voiceoverOff', defaultMessage: 'Voiceover off' },
  other: { id: 'app.sml.promote.reels.reject.chip.other', defaultMessage: 'Other' },
});

/** The status pill of a reel (design c). A working reel shows its stage name. */
export const reelStatusMessages: Record<ReelStatus, MessageDescriptor> = defineMessages({
  queued: { id: 'app.sml.promote.reels.status.queued', defaultMessage: 'Queued' },
  analysing: { id: 'app.sml.promote.reels.status.analysing', defaultMessage: 'Analysing' },
  planning: { id: 'app.sml.promote.reels.status.planning', defaultMessage: 'Planning' },
  rendering: { id: 'app.sml.promote.reels.status.rendering', defaultMessage: 'Rendering' },
  ready: { id: 'app.sml.promote.reels.status.ready', defaultMessage: 'Ready' },
  approved: { id: 'app.sml.promote.reels.status.approved', defaultMessage: 'Approved' },
  rejected: { id: 'app.sml.promote.reels.status.rejected', defaultMessage: 'Rejected' },
  failed: { id: 'app.sml.promote.reels.status.failed', defaultMessage: 'Failed' },
});

/** The label of a status this app does not know yet (one a newer API sends). */
const reelStatusUnknownMessage = defineMessage({
  id: 'app.sml.promote.reels.status.unknown',
  defaultMessage: 'Unknown',
});

/** The label of a reel status, with a neutral label for a status from a newer API. */
export function reelStatusMessage(status: ReelStatus): MessageDescriptor {
  return Object.prototype.hasOwnProperty.call(reelStatusMessages, status)
    ? reelStatusMessages[status]
    : reelStatusUnknownMessage;
}

/** The short form of each fail sentence, for the history strip (design c). */
export const reelFailShortMessages: Record<ReelFailKey, MessageDescriptor> = {
  ...defineMessages({
    SONG_TOO_SHORT: { id: 'app.sml.promote.reels.failShort.songTooShort', defaultMessage: 'Song too short' },
    UNUSABLE_AUDIO: { id: 'app.sml.promote.reels.failShort.unusableAudio', defaultMessage: 'Audio not readable' },
    ANALYSIS_FAILED: { id: 'app.sml.promote.reels.failShort.analysisFailed', defaultMessage: 'Analysis failed' },
    DIRECTOR_FAILED: { id: 'app.sml.promote.reels.failShort.directorFailed', defaultMessage: 'No plan made' },
    BUDGET_EXCEEDED: { id: 'app.sml.promote.reels.failShort.budgetExceeded', defaultMessage: 'Paused this month' },
    VOICEOVER_FAILED: { id: 'app.sml.promote.reels.failShort.voiceoverFailed', defaultMessage: 'Voiceover failed' },
    RENDER_FAILED: { id: 'app.sml.promote.reels.failShort.renderFailed', defaultMessage: 'Render failed' },
    RENDER_TIMEOUT: { id: 'app.sml.promote.reels.failShort.renderTimeout', defaultMessage: 'Took too long' },
    STALLED: { id: 'app.sml.promote.reels.failShort.stalled', defaultMessage: 'Stopped' },
    unknown: { id: 'app.sml.promote.reels.failShort.unknown', defaultMessage: 'Did not finish' },
  }),
  AI_BUDGET_EXHAUSTED: aiBudgetMessages.failShort,
};
